import { useState } from 'react'
import { calculateNewScore } from '@/src/utils/scoreUtils'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { getActionPlans } from '@/infra/api/generated/action-plans/action-plans'
import { ActionPlanData, ActionPlanItem, ActionPlanStatus } from '@/infra/api/generated/model'
import { trackEvent } from '@/src/analytics/amplitude'

const { validateAction, completeActionPlan } = getActionPlans()

const useActionUpdate = (
  actionPlanDetails: ActionPlanData,
  openCompletionModalPromise: () => Promise<boolean>,
  openCustomActionsRemainingModalPromise: () => Promise<boolean>
) => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [actionList, setActionList] = useState(actionPlanDetails.actions || [])
  const [isListDisabled, setIsListDisabled] = useState(
    actionPlanDetails.status !== ActionPlanStatus.IN_PROGRESS
  )
  const [actionPlanScoreValue, setActionPlanScoreValue] = useState(actionPlanDetails.score)
  const [actionPlanLastUpdatedAt, setActionPlanLastUpdatedAt] = useState(
    actionPlanDetails.lastUpdatedAt
  )

  const handleCompletionLogic = async (actionPlanItemId: string): Promise<boolean> => {
    const userConfirmed = await openCompletionModalPromise()
    if (userConfirmed) {
      await validateAction(actionPlanDetails.id, {
        actionPlanItemId,
        value: true,
      })
      await completeActionPlan(actionPlanDetails.id)
      return true // Succès et navigation après
    } else {
      const originalActionList = actionList.map((action) =>
        action.actionPlanItemId === actionPlanItemId ? { ...action, done: false } : action
      )
      setActionList(originalActionList)
      setActionPlanScoreValue(calculateNewScore(originalActionList)) // Score avant la modif
      return false // Annulation de l'action
    }
  }

  const handleSimpleUpdate = async (actionPlanItemId: string, done: boolean) => {
    await validateAction(actionPlanDetails.id, { actionPlanItemId, value: done })
  }

  // Returns true when the user chooses to continue with the remaining custom actions
  const handleRemainingCustomActionsLogic = async (actionPlanItemId: string): Promise<boolean> => {
    await handleSimpleUpdate(actionPlanItemId, true)
    return openCustomActionsRemainingModalPromise()
  }

  const handleActionUpdate = async (actionPlanItem: ActionPlanItem, done: boolean) => {
    setIsListDisabled(true)

    const newActions = actionList.map((action) =>
      action.actionPlanItemId === actionPlanItem.actionPlanItemId ? { ...action, done } : action
    )
    setActionList(newActions)
    const newScore = calculateNewScore(newActions)
    setActionPlanScoreValue(newScore)
    setActionPlanLastUpdatedAt(new Date().toString())
    const hasPendingCustomActions = newActions.some((action) => action.isCustom && !action.done)
    if (done && newScore === 100 && !hasPendingCustomActions) {
      const isCompleted = await handleCompletionLogic(actionPlanItem.actionPlanItemId)
      if (isCompleted) {
        void navigate(`/my-hub/diagnosis`, { viewTransition: true })
      }
    } else if (done && newScore === 100 && !actionPlanItem.isCustom) {
      const shouldContinue = await handleRemainingCustomActionsLogic(
        actionPlanItem.actionPlanItemId
      )
      if (!shouldContinue) {
        void navigate(`/my-hub/diagnosis`, { viewTransition: true })
      }
    } else {
      await handleSimpleUpdate(actionPlanItem.actionPlanItemId, done)
    }
    if (done)
      trackEvent('ACTION_CHECKED', {
        actionPlanId: actionPlanDetails.id,
        actionId: actionPlanItem.actionPlanItemId,
      })
    else
      trackEvent('ACTION_UNCHECKED', {
        actionPlanId: actionPlanDetails.id,
        actionId: actionPlanItem.actionPlanItemId,
      })
    await queryClient.invalidateQueries({ queryKey: ['actions'] })
    await queryClient.invalidateQueries({ queryKey: ['diagnoses', 'actions'] })
    await queryClient.invalidateQueries({ queryKey: ['diagnoses', 'history'] })
    await queryClient.invalidateQueries({ queryKey: ['diagnoses', 'vendors', 'detail'] })
    await queryClient.invalidateQueries({ queryKey: ['diagnoses', 'vendors', 'summary'] })
    await queryClient.invalidateQueries({ queryKey: ['priorityDiagnoses'] })

    setIsListDisabled(false)
  }

  return {
    handleActionUpdate,
    isListDisabled,
    actionPlanScoreValue,
    actionList,
    actionPlanLastUpdatedAt,
  }
}

export default useActionUpdate
